/**
 * Карта «Алматы» для «Полёта птицы» — открытый мир на острове R = 1900.
 * Позиции — по реальным координатам (`geo(lat, lon)`): площадь Республики в
 * центре, до 3,5 км — 270 ед/км, дальше расстояние сжимается, чтобы Медеу и
 * Большое Алматинское озеро поместились в горах на юге. Город — сетка улиц;
 * гостиница «Казахстан», Дворец Республики, площадь с акиматом, собор в парке
 * панфиловцев, КБТУ, мечеть, Нурлы Тау, Esentai, Сайран, Кок-Тобе с канаткой,
 * яблоневые сады, реки Большая Алматинка и Есентай. Всё из примитивов по
 * фото-референсам, мир детерминирован по seed.
 * Время суток: 0 — день, 0,5 — закат, 1 — ночь с огнями.
 *
 * Перенесено из прототипа groove-bird-prototype/almaty/map.src.html.
 */
/* eslint-disable */
import * as THREE from 'three';
import type { BirdFlightWorld } from './ekibastuzWorld.ts';
import {
  distanceCuller, facadeMaterial, instancedBySector, mergeStaticMeshes,
} from './worldOptimize.ts';

export function* buildAlmatyWorld(scene: THREE.Scene): Generator<number, BirdFlightWorld, void> {
  // ─── Случайность по seed: одинаковый мир у всех игроков ───────────────────
  function mulberry32(a) {
    return function () {
      a |= 0; a = a + 0x6D2B79F5 | 0;
      let t = Math.imul(a ^ a >>> 15, 1 | a);
      t = t + Math.imul(t ^ t >>> 7, 61 | t) ^ t;
      return ((t ^ t >>> 14) >>> 0) / 4294967296;
    };
  }
  const rnd = mulberry32(20260929);
  const rand = (a, b) => a + rnd() * (b - a);
  const pick = (list) => list[Math.floor(rnd() * list.length)];
  const smooth = (a, b, x) => { const t = Math.min(1, Math.max(0, (x - a) / (b - a))); return t * t * (3 - 2 * t); };

  yield 0.05;
  // ─── Небо: шейдерная сфера, цвета меняются со временем суток ─────────────
  const skyUniforms = {
    top: { value: new THREE.Color() }, mid: { value: new THREE.Color() }, horizon: { value: new THREE.Color() },
    sunDir: { value: new THREE.Vector3() }, sunColor: { value: new THREE.Color() }, sunStrength: { value: 1 },
  };
  const sky = new THREE.Mesh(new THREE.SphereGeometry(1400, 32, 16), new THREE.ShaderMaterial({
    side: THREE.BackSide, depthWrite: false, fog: false, uniforms: skyUniforms,
    vertexShader: 'varying vec3 vDir; void main(){ vDir = normalize(position); gl_Position = projectionMatrix * modelViewMatrix * vec4(position,1.0); }',
    fragmentShader: `
      uniform vec3 top, mid, horizon, sunColor, sunDir; uniform float sunStrength; varying vec3 vDir;
      void main(){
        float h = vDir.y;
        vec3 c = h > 0.18 ? mix(mid, top, smoothstep(0.18, 0.8, h)) : mix(horizon, mid, smoothstep(-0.02, 0.18, h));
        float d = max(dot(normalize(vDir), normalize(sunDir)), 0.0);
        c += sunColor * (pow(d, 2500.0) * 4.0 + pow(d, 90.0) * 0.35) * sunStrength;
        gl_FragColor = vec4(c, 1.0);
        #include <colorspace_fragment>
      }`,
  }));
  scene.add(sky);

  // Звёзды на ночном небе.
  const nightStars = (() => {
    const pts = [];
    for (let i = 0; i < 1400; i++) {
      const u = rnd() * Math.PI * 2, v = Math.acos(rand(0.05, 1));
      pts.push(Math.sin(v) * Math.cos(u) * 1350, Math.cos(v) * 1350, Math.sin(v) * Math.sin(u) * 1350);
    }
    const g = new THREE.BufferGeometry(); g.setAttribute('position', new THREE.Float32BufferAttribute(pts, 3));
    const p = new THREE.Points(g, new THREE.PointsMaterial({ color: 0xffffff, size: 2.2, sizeAttenuation: false, transparent: true, fog: false }));
    scene.add(p); return p;
  })();

  // Туман ближе, как в Экибастузе: дальше 1300 м не видно, и секторы
  // за туманом отсекаются камерой — меньше работы ТВ.
  // Тумана по сути нет: он включается только у самой дальней границы
  // (1150–1380 м) — прячет край острова и обрезку по camera.far (сфера неба
  // 1400 м). Бежевый туман с 400 м, как у Астаны, выглядел смогом.
  const FOG_NEAR_DAY = 1150, FOG_NEAR_NIGHT = 900;
  const fog = new THREE.Fog(0xffffff, FOG_NEAR_DAY, 1380);
  scene.fog = fog;
  const hemi = new THREE.HemisphereLight(0xffffff, 0x3a3048, 1);
  scene.add(hemi);
  const sun = new THREE.DirectionalLight(0xffffff, 1);
  scene.add(sun);

  const lambert = (color, extra = {}) => new THREE.MeshLambertMaterial({ color, flatShading: true, ...extra });
  const box = new THREE.BoxGeometry(1, 1, 1);

  // Огни: днём тусклые, ночью горят. Каждый — материал + дневной и ночной цвет.
  const lights = [];
  function lightMat(dayColor, nightColor) {
    const m = new THREE.MeshBasicMaterial({ color: dayColor });
    lights.push({ m, day: new THREE.Color(dayColor), night: new THREE.Color(nightColor) });
    return m;
  }

  // Инстансинг: сотни одинаковых объектов — один draw call.
  // Инстансинг по секторам — куски вне камеры не рисуются (как в Экибастузе).
  // Сектор крупнее, чем у Экибастуза (400): остров вдвое больше, и мелкие
  // секторы дробили каждый вид объектов — замер у старта 296 → 365 вызовов,
  // с 1300 м — 201.
  const SECTOR_SIZE = 1300;
  function instanced(geo, material, list) {
    if (list.length) instancedBySector(scene, geo, material, list, SECTOR_SIZE);
  }
  function add(geo, material, x, y, z, sx = 1, sy = 1, sz = 1, ry = 0) {
    const m = new THREE.Mesh(geo, material);
    m.position.set(x, y, z); m.scale.set(sx, sy, sz); m.rotation.y = ry; scene.add(m); return m;
  }

  yield 0.10;
  // ─── Алматы: город у гор ─────────────────────────────────────────────────
  // Север — ровный зелёный город сеткой улиц. Юг — Заилийский Алатау:
  // предгорья, скалы, снежные вершины. Кок-Тобе — холм на востоке с
  // телебашней и канаткой. Медеу — каток в долине, выше плотина. Большое
  // Алматинское озеро — бирюзовое, в горах на юго-западе.
  const ISLAND = 1900;
  const redLights = [];

  // Реальные координаты → мир. (0,0) — площадь Республики, x < 0 — восток,
  // z > 0 — север. Центр до 3,5 км — честно, 270 ед/км; дальше расстояние
  // сжимается (Сайран ~1190, Медеу ~1570, БАО ~1750), чтобы горы влезли в остров.
  const ORIGIN = { lat: 43.2383, lon: 76.9453 };
  const KM_LAT = 111.2, KM_LON = 81.0, SCALE = 270, NEAR_KM = 3.5;
  function geo(lat, lon) {
    const n = (lat - ORIGIN.lat) * KM_LAT, e = (lon - ORIGIN.lon) * KM_LON, d = Math.hypot(n, e);
    const r = d <= NEAR_KM ? d * SCALE : NEAR_KM * SCALE + 900 * (1 - Math.exp(-(d - NEAR_KM) / 8));
    const k = d ? r / d : 0;
    return { x: -e * k, z: n * k };
  }
  const P = {
    hotel: geo(43.2448, 76.9579),
    palace: geo(43.2420, 76.9580),
    cathedral: geo(43.2587, 76.9533),
    kbtu: geo(43.2560, 76.9445),
    mosque: geo(43.2690, 76.9493),
    nurlytau: geo(43.2248, 76.9445),
    esentai: geo(43.2181, 76.9277),
    sairan: geo(43.2385, 76.8710),
    apples: geo(43.2050, 76.8850),
  };
  const MEDEU = geo(43.1573, 77.0590);
  const LAKE = { ...geo(43.0510, 76.9850), r: 110 };
  const KOKTOBE = { ...geo(43.2330, 76.9760), h: 110 };
  // Горы: подножие по z (юг). Предгорья с Аль-Фараби (~2,5 км), стена — дальше.
  const HILLS = { from: -740, to: -1250 };

  yield 0.14;
  // ─── Реки: Большая Алматинка (через Сайран) и Есентай (мимо Esentai Tower) ─
  // Текут с гор на север в бетонных руслах. Точки — реальные lat/lon.
  const RIVERS = [
    { w: 14, pts: [[43.150, 76.884], [43.180, 76.879], [43.205, 76.875], [43.2385, 76.871], [43.255, 76.866], [43.275, 76.860], [43.300, 76.855]] },
    { w: 9, pts: [[43.170, 76.938], [43.195, 76.934], [43.2181, 76.9290], [43.232, 76.923], [43.248, 76.915], [43.265, 76.906], [43.290, 76.896]] },
  ].map((r) => {
    // Сгущаем ломаную до шага ~12 ед, чтобы русло шло по рельефу.
    const g = r.pts.map(([la, lo]) => geo(la, lo)), pts = [];
    for (let i = 0; i < g.length - 1; i++) {
      const a = g[i], b = g[i + 1], n = Math.max(1, Math.ceil(Math.hypot(b.x - a.x, b.z - a.z) / 12));
      for (let k = 0; k < n; k++) pts.push({ x: a.x + (b.x - a.x) * k / n, z: a.z + (b.z - a.z) * k / n });
    }
    pts.push(g[g.length - 1]);
    // line — исходная ломаная (6 звеньев): для расстояния до реки её хватает,
    // сгущённые точки лежат на тех же отрезках. Перебор сгущённых (~500
    // отрезков) на каждый вызов давал 3,6 + 1,8 с постройки на ТВ.
    return { w: r.w, pts, line: g };
  });
  // Расстояние до ближайшей реки — для ущелий в горах.
  function riverDist(x, z) {
    let best = Infinity;
    RIVERS.forEach(({ line }) => line.forEach((a, i) => {
      const b = line[i + 1]; if (!b) return;
      const dx = b.x - a.x, dz = b.z - a.z, t = Math.max(0, Math.min(1, ((x - a.x) * dx + (z - a.z) * dz) / (dx * dx + dz * dz)));
      best = Math.min(best, Math.hypot(x - a.x - dx * t, z - a.z - dz * t));
    }));
    return best;
  }
  function nearRiver(x, z, m = 0) {
    return RIVERS.some(({ w, line }) => line.some((a, i) => {
      const b = line[i + 1]; if (!b) return false;
      const dx = b.x - a.x, dz = b.z - a.z, t = Math.max(0, Math.min(1, ((x - a.x) * dx + (z - a.z) * dz) / (dx * dx + dz * dz)));
      return Math.hypot(x - a.x - dx * t, z - a.z - dz * t) < w / 2 + 3 + m;
    }));
  }
  function height(x, z) {
    // Горы растут к югу, гребень неровный.
    // Горы начинаются примерно в 3 км к югу от площади Республики.
    const m = smooth(HILLS.from, HILLS.to, z);
    let h = m * (150 + 80 * Math.sin(x * 0.005 + 1.3) * Math.cos(z * 0.006) + 35 * Math.sin(x * 0.018 + z * 0.008));
    h += smooth(-1450, -1800, z) * 60;
    // Кок-Тобе — отдельный холм.
    h += KOKTOBE.h * Math.exp(-((x - KOKTOBE.x) ** 2 + (z - KOKTOBE.z) ** 2) / (2 * 150 * 150));
    // Долина Медеу: прорезаем горы, дно ~40 м.
    const valley = Math.exp(-((x - MEDEU.x) ** 2) / (2 * 90 * 90)) * smooth(HILLS.from + 40, HILLS.from - 200, z) * (1 - smooth(MEDEU.z - 200, MEDEU.z - 320, z));
    h = THREE.MathUtils.lerp(h, 40, valley * 0.85);
    // Ущелья рек в горах: русло режет склон почти до дна.
    if (z < HILLS.from + 80 && h > 5) {
      const gorge = Math.exp(-(riverDist(x, z) ** 2) / (2 * 40 * 40)) * smooth(HILLS.from + 80, HILLS.from - 60, z);
      h = THREE.MathUtils.lerp(h, h * 0.3, gorge);
    }
    // За краем острова — холмы, чтобы не было обрыва.
    h += smooth(ISLAND * 0.85, ISLAND * 1.15, Math.hypot(x, z)) * 40;
    // Ровная площадка под катком Медеу.
    const md = Math.hypot(x - MEDEU.x, (z - MEDEU.z) * 1.3);
    if (md < 110) h = THREE.MathUtils.lerp(40, h, smooth(70, 110, md));
    // Озеро — ровная чаша (после края острова, иначе вода тонет).
    const lk = Math.hypot(x - LAKE.x, z - LAKE.z);
    if (lk < LAKE.r * 1.8) h = THREE.MathUtils.lerp(118, h, smooth(LAKE.r * 1.05, LAKE.r * 1.8, lk));
    return Math.max(0, h);
  }

  {
    const seg = 240;
    // Высоты считаем по узлам сетки (241² ≈ 58 тыс.) и только потом
    // разворачиваем в треугольники: раньше height() звалась на каждую
    // вершину развёрнутой сетки — в 6 раз чаще (~350 тыс. вызовов, ¾ всей
    // постройки карты; на ТВ это секунды загрузки).
    const grid = new THREE.PlaneGeometry(ISLAND * 2.6, ISLAND * 2.6, seg, seg);
    grid.rotateX(-Math.PI / 2);
    const gridPos = grid.attributes.position;
    for (let i = 0; i < gridPos.count; i++) gridPos.setY(i, height(gridPos.getX(i), gridPos.getZ(i)));
    const geo = grid.toNonIndexed();
    grid.dispose();
    const pos = geo.attributes.position;
    const city = [0x7fae5a, 0x76a653, 0x86b563].map((c) => new THREE.Color(c));
    const forest = [0x3f7a45, 0x4a8650, 0x376c3e].map((c) => new THREE.Color(c));
    const rock = [0x8a8076, 0x7d746b, 0x958b80].map((c) => new THREE.Color(c));
    const snow = [0xf4f7fb, 0xe6edf5].map((c) => new THREE.Color(c));
    const colors = [];
    for (let f = 0; f < pos.count; f += 3) {
      const y = (pos.getY(f) + pos.getY(f + 1) + pos.getY(f + 2)) / 3;
      const c = y > 205 ? pick(snow) : y > 140 ? pick(rock) : y > 25 ? pick(forest) : pick(city);
      for (let k = 0; k < 3; k++) colors.push(c.r, c.g, c.b);
    }
    geo.setAttribute('color', new THREE.Float32BufferAttribute(colors, 3));
    geo.computeVertexNormals();
    scene.add(new THREE.Mesh(geo, new THREE.MeshLambertMaterial({ vertexColors: true, flatShading: true })));
  }

  yield 0.19;
  // ─── Общие строители ─────────────────────────────────────────────────────
  const lampPoles = [], lampHeads = [];
  const roadMat = lambert(0x44464b);
  const lineMat = lightMat(0xcfc6b0, 0xf2e6c8);
  // Участки достопримечательностей: улицы сквозь них не идут, фонарей нет.
  // [центр x, центр z, полуширина по x, полуширина по z].
  const LOTS = [
    [0, -90, 115, 140],
    [P.hotel.x, P.hotel.z, 30, 20], [P.palace.x + 10, P.palace.z, 40, 30],
    [P.cathedral.x, P.cathedral.z, 95, 90],
    [P.kbtu.x, P.kbtu.z - 15, 85, 62],
    [P.mosque.x, P.mosque.z, 40, 40],
    [P.esentai.x - 20, P.esentai.z + 8, 50, 30],
    [P.nurlytau.x, P.nurlytau.z - 10, 85, 70],
    [P.sairan.x - 5, P.sairan.z - 5, 75, 60],
    [P.apples.x + 5, P.apples.z, 100, 80],
  ];
  const inLot = (x, z, m = 0) => LOTS.some(([cx, cz, hx, hz]) => Math.abs(x - cx) < hx + m && Math.abs(z - cz) < hz + m);

  const ROAD_HALF = 6, STEP_X = 140, STEP_Z = 110;
  // Точка на полотне улицы (с запасом m), с учётом разрывов у участков.
  function onRoad(x, z, m = 0) {
    if (x < CITY.x0 - ROAD_HALF - m || x > CITY.x1 + ROAD_HALF + m || z < CITY.z0 - ROAD_HALF - m || z > CITY.z1 + ROAD_HALF + m) return false;
    if (inLot(x, z)) return false;
    const gx = CITY.x0 + Math.round((x - CITY.x0) / STEP_X) * STEP_X, gz = CITY.z0 + Math.round((z - CITY.z0) / STEP_Z) * STEP_Z;
    return Math.abs(x - gx) < ROAD_HALF + m || Math.abs(z - gz) < ROAD_HALF + m;
  }
  // Прямая улица, разрезанная участками: рисуются только куски вне них.
  function streetLine(x1, z1, x2, z2) {
    const len = Math.hypot(x2 - x1, z2 - z1), stepLen = 2;
    let start = null;
    for (let sPos = 0; sPos <= len; sPos += stepLen) {
      const x = x1 + (x2 - x1) * sPos / len, z = z1 + (z2 - z1) * sPos / len;
      const free = !inLot(x, z, ROAD_HALF);
      if (free && start === null) start = sPos;
      if ((!free || sPos + stepLen > len) && start !== null) {
        const e = free ? sPos : sPos - stepLen;
        if (e - start > 4) road(x1 + (x2 - x1) * start / len, z1 + (z2 - z1) * start / len, x1 + (x2 - x1) * e / len, z1 + (z2 - z1) * e / len, ROAD_HALF * 2, 24);
        start = null;
      }
    }
  }
  function road(x1, z1, x2, z2, w, lamps = 0) {
    const dx = x2 - x1, dz = z2 - z1, len = Math.hypot(dx, dz), ry = Math.atan2(dx, dz);
    add(box, roadMat, (x1 + x2) / 2, 0.35, (z1 + z2) / 2, w, 0.3, len, ry);
    add(box, lineMat, (x1 + x2) / 2, 0.55, (z1 + z2) / 2, 0.3, 0.05, len, ry);
    if (lamps) for (let s = 0; s <= len; s += lamps) {
      const x = x1 + dx * s / len + Math.cos(ry) * (w / 2 + 1), z = z1 + dz * s / len - Math.sin(ry) * (w / 2 + 1);
      lampPoles.push({ x, y: 4, z, sx: 0.25, sy: 8, sz: 0.25 });
      lampHeads.push({ x, y: 8.2, z, sx: 0.9, sy: 0.5, sz: 0.9 });
    }
  }
  const winOn = [], winOff = [], trunks = [], crowns = [], apples = [];
  const winLit = lightMat(0x5a6470, 0xffd58a);
  const winDark = lightMat(0x5a6470, 0x28303e);
  // Окна обычных домов — в шейдере фасада (`facadeMaterial`), не кубиками.
  const facadeGlow = { value: 0 };
  const facade = (color) => facadeMaterial(color, facadeGlow);
  const panelMats = [facade(0xe6e2d8), facade(0xd9dce0), facade(0xeadfca), facade(0xd8cfc0)];
  const glassMats = [facade(0x5d8fb8), facade(0x6fa3c7), facade(0x4f7ea8)];
  // Корпуса и крыши копятся по материалу и рисуются инстансами в конце.
  const bodies = new Map(), roofMat = lambert(0x7c8288);
  function addBody(mat, it) { if (!bodies.has(mat)) bodies.set(mat, []); bodies.get(mat).push(it); }
  function tower(x, z, w, d, h, mat, y0 = 0) {
    addBody(mat, { x, y: y0 + h / 2, z, sx: w, sy: h, sz: d });
    addBody(roofMat, { x, y: y0 + h + 0.4, z, sx: w + 0.4, sy: 0.8, sz: d + 0.4 });
    // Окна — в шейдере фасада. Случайные числа тратим как раньше, когда
    // окна были кубиками: иначе сдвинулась бы раскладка всего города.
    for (let y = y0 + 3; y < y0 + h - 2; y += 3.4) {
      for (let u = -w / 2 + 1.5; u < w / 2 - 1; u += 2.6) { rnd(); rnd(); }
      for (let u = -d / 2 + 1.5; u < d / 2 - 1; u += 2.6) { rnd(); rnd(); }
    }
  }
  function tree(x, z, y = 0) {
    if (onRoad(x, z, 3) || nearRiver(x, z, 1)) return;
    const h = rand(6, 10);
    trunks.push({ x, y: y + h * 0.35, z, sx: 0.45, sy: h * 0.7, sz: 0.45 });
    crowns.push({ x, y: y + h * 0.8, z, sx: rand(2.2, 3.4), sy: h * 0.45, sz: rand(2.2, 3.4), ry: rnd() * 3 });
  }
  function spruce(x, z) {
    const y = height(x, z), h = rand(8, 14);
    trunks.push({ x, y: y + 1, z, sx: 0.5, sy: 2, sz: 0.5 });
    crowns.push({ x, y: y + h / 2 + 1, z, sx: 2.4, sy: h, sz: 2.4, spruce: true });
  }

  yield 0.24;
  // ─── Город: сетка улиц, дома, аллеи ──────────────────────────────────────
  // Улицы идут строго по сторонам света, как в Алматы; проспекты — с
  // деревьями по обе стороны.
  const CITY = { x0: -1400, x1: 1400, z0: -700, z1: 1500 };
  {
    for (let x = CITY.x0; x <= CITY.x1; x += STEP_X) streetLine(x, CITY.z0, x, CITY.z1);
    for (let z = CITY.z0; z <= CITY.z1; z += STEP_Z) streetLine(CITY.x0, z, CITY.x1, z);
    const reserved = [[0, 0, 0, 70], ['hotel', 50], ['palace', 45], ['cathedral', 110], ['mosque', 60], ['esentai', 70], ['nurlytau', 80], ['kbtu', 60], ['sairan', 110], ['apples', 150]]
      .map((r) => (r.length === 2 ? [P[r[0]].x, P[r[0]].z, r[1]] : [r[0], r[1], r[3]]));
    reserved.push([KOKTOBE.x, KOKTOBE.z, 220]);
    for (let bx = CITY.x0 + 70; bx < CITY.x1; bx += 140) {
      for (let bz = CITY.z0 + 55; bz < CITY.z1; bz += 110) {
        if (Math.hypot(bx, bz) > ISLAND * 0.85) continue;
        if (Math.hypot(bx, bz) > 1100 && rnd() < 0.45) continue; // окраины реже
        if (reserved.some(([rx, rz, rr]) => Math.hypot(bx - rx, bz - rz) < rr)) continue;
        if (inLot(bx, bz, 60)) continue;
        if (height(bx, bz) > 5) continue;
        const south = bz < 300; // ближе к горам — выше и новее
        const n = rnd() < 0.5 ? 2 : 3;
        for (let k = 0; k < n; k++) {
          const w = rand(22, 40), d = rand(14, 22), h = south && rnd() < 0.4 ? rand(35, 70) : rand(12, 24);
          const tx = bx + rand(-35, 35), tz = bz + (k - (n - 1) / 2) * 32;
          if (nearRiver(tx, tz, Math.max(w, d) / 2)) continue;
          tower(tx, tz, w, d, h, south && h > 34 ? pick(glassMats) : pick(panelMats));
        }
        for (let k = 0; k < 6; k++) tree(bx + rand(-60, 60), bz + rand(-45, 45));
      }
    }
    // Аллеи вдоль проспектов.
    for (let x = CITY.x0; x <= CITY.x1; x += 140) for (let z = CITY.z0; z < CITY.z1; z += 9) [-9, 9].forEach((o) => { if (rnd() < 0.8 && height(x + o, z) < 3 && !inLot(x + o, z)) tree(x + o, z); });
  }

  yield 0.29;
  // ─── Русла рек (после сетки улиц: на пересечениях — мосты) ─────────────
  {
    const banks = [], water = [];
    RIVERS.forEach(({ w, pts }) => pts.forEach((a, i) => {
      const b = pts[i + 1]; if (!b) return;
      const mx = (a.x + b.x) / 2, mz = (a.z + b.z) / 2, flat = Math.hypot(b.x - a.x, b.z - a.z), ry = Math.atan2(b.x - a.x, b.z - a.z);
      if (Math.hypot(mx - P.sairan.x, mz - P.sairan.z) < 50) return;       // в Сайране — само озеро
      if (onRoad(mx, mz, 2)) return;                                       // под мостом — дорога сверху
      // На склоне участок наклоняем вдоль течения, чтобы русло не шло ступеньками.
      const ya = height(a.x, a.z), yb = height(b.x, b.z), y = (ya + yb) / 2, len = Math.hypot(flat, yb - ya) + 1;
      const q = new THREE.Quaternion().setFromEuler(new THREE.Euler(-Math.atan2(yb - ya, flat), ry, 0, 'YXZ'));
      banks.push({ x: mx, y: y + 0.25, z: mz, sx: w + 5, sy: 0.8, sz: len, q });
      water.push({ x: mx, y: y + 0.55, z: mz, sx: w, sy: 0.3, sz: len, q });
    }));
    instanced(box, lambert(0xb4b0a8), banks);
    instanced(box, new THREE.MeshPhongMaterial({ color: 0x5fa8b8, specular: 0xffffff, shininess: 70 }), water);
  }

  yield 0.33;
  // ─── Площадь Республики ──────────────────────────────────────────────────
  // С севера на юг, как на фото: стела Независимости с золотым воином →
  // проспект → ТЦ под площадью (стеклянный купол, белые изогнутые навесы) →
  // террасы-ступени с газоном → длинный белый акимат. По бокам — парк.
  {
    const x = 0, z = 0;
    const stone = lambert(0xa9825a), stoneLight = lambert(0xbf9a70), paving = lambert(0xd9d3c6);
    const gold = new THREE.MeshLambertMaterial({ color: 0xd9a93a, emissive: 0x2a1c00 });
    // Стела: ярусы коричневого камня, выше — уже, «медальоны» по сторонам.
    add(new THREE.CylinderGeometry(16, 16, 0.6, 32), lambert(0xc98f6a), x, 0.3, z);
    add(box, stone, x, 2, z, 12, 4, 12);
    let y = 4;
    [[6, 7], [5, 3], [4.6, 7], [4, 3], [3.6, 7], [3, 3], [2.6, 6]].forEach(([w, hh], k) => {
      add(box, k % 2 ? stoneLight : stone, x, y + hh / 2, z, w, hh, w);
      if (k % 2 === 0) [[0, 1], [0, -1], [1, 0], [-1, 0]].forEach(([dx, dz]) => { const m = add(new THREE.CylinderGeometry(w * 0.3, w * 0.3, 0.3, 16), stoneLight, x + dx * (w / 2 + 0.1), y + hh / 2, z + dz * (w / 2 + 0.1)); m.rotation.x = dz ? Math.PI / 2 : 0; m.rotation.z = dx ? Math.PI / 2 : 0; });
      y += hh;
    });
    // Барс и воин в остроконечном шлеме.
    add(box, gold, x, y + 1, z, 2.2, 2, 4.5);
    add(new THREE.CylinderGeometry(0.7, 0.9, 5, 8), gold, x, y + 4.5, z);
    add(new THREE.SphereGeometry(0.6, 8, 6), gold, x, y + 7.4, z);
    add(new THREE.ConeGeometry(0.55, 2.2, 8), gold, x, y + 8.9, z);
    [-1, 1].forEach((sd) => { const w = add(box, gold, x + sd * 1.8, y + 6, z, 2.6, 0.3, 1.2); w.rotation.z = sd * 0.6; });

    // Проспект перед стелой — широкий, с разметкой.
    road(-110, z - 30, 110, z - 30, 22, 20);
    // Площадь над ТЦ: круг с куполом, два белых навеса-«крыла».
    const pz = z - 78;
    add(box, paving, x, 0.3, pz, 150, 0.4, 70);
    add(new THREE.CylinderGeometry(26, 26, 0.5, 40), lambert(0xc4bdb0), x, 0.6, pz);
    add(new THREE.SphereGeometry(13, 24, 12, 0, Math.PI * 2, 0, Math.PI / 2), new THREE.MeshPhongMaterial({ color: 0x3fb8b0, specular: 0xffffff, shininess: 90, transparent: true, opacity: 0.85 }), x, 0.6, pz);
    const canopy = new THREE.MeshLambertMaterial({ color: 0xeceeea, side: THREE.DoubleSide });
    [-1, 1].forEach((sd) => {
      const arc = add(new THREE.CylinderGeometry(40, 40, 0.8, 32, 1, false, 0, Math.PI * 0.45), canopy, x + sd * 30, 7, pz - 6);
      arc.rotation.y = sd > 0 ? -Math.PI * 0.2 : Math.PI * 0.75;
      arc.scale.set(1, 1, 0.6);
      add(box, lambert(0x4a4f55), x + sd * 42, 3, pz - 20, 36, 6, 10);   // павильоны-входы
    });
    // Террасы-ступени к акимату, газон в центре.
    for (let k = 0; k < 5; k++) add(box, paving, x, 0.8 + k * 0.9, z - 118 - k * 7, 120 - k * 6, 1.6 + k * 1.8, 8);
    add(box, lambert(0x6fae4f), x, 5.4, z - 132, 22, 0.3, 18);
    // Акимат: длинный белый корпус с сеткой вертикальных окон, бежевые торцы.
    const az = z - 175, aw = 120, ah = 28;
    add(box, lambert(0xf1efe8), x, 5 + ah / 2, az, aw, ah, 24);
    add(box, lambert(0xd9c7a0), x - aw / 2 + 5, 5 + (ah + 3) / 2, az, 10, ah + 3, 26);
    add(box, lambert(0xd9c7a0), x + aw / 2 - 5, 5 + (ah + 3) / 2, az, 10, ah + 3, 26);
    add(box, lambert(0xe2dccd), x, 5 + ah + 0.6, az, aw + 1, 1.2, 25);
    add(box, lambert(0xe8e2d4), x, 2.5, az, aw + 6, 5, 28);            // стилобат
    for (let u = -aw / 2 + 12; u < aw / 2 - 11; u += 2.4) for (let yy = 8; yy < 5 + ah - 2; yy += 3.4) (rnd() < 0.5 ? winOn : winOff).push({ x: x + u, y: yy, z: az + 12.08, sx: 1.2, sy: 2.6, sz: 0.1 });
    add(box, lambert(0xb8864a), x, 8, az + 12.3, 8, 6, 0.4);            // входной портал
    add(new THREE.CircleGeometry(2.2, 20), gold, x, 5 + ah - 3, az + 12.2);
    // Парк по бокам площади.
    for (let k = 0; k < 140; k++) { const sd = rnd() < 0.5 ? -1 : 1; tree(x + sd * rand(80, 110), z + rand(-190, -40)); }
    for (let k = 0; k < 30; k++) spruce(x + (rnd() < 0.5 ? -1 : 1) * rand(66, 78), z + rand(-160, -50));
  }

  yield 0.38;
  // ─── Гостиница «Казахстан»: план-«линза», песочный фасад с лоджиями,
  // тёмная полоса остекления наверху и золотая корона из шпилей ─────────────
  {
    const { x, z } = P.hotel, h = 100, W = 17;
    const hd = (u) => 3 + 5.5 * (1 - (u / W) ** 2);        // полуглубина: 8,5 в центре, 3 по краям
    const lens = (k = 1) => {
      const sh = new THREE.Shape(), n = 24;
      for (let i = 0; i <= n; i++) { const u = -W + 2 * W * i / n; i ? sh.lineTo(u * k, hd(u) * k) : sh.moveTo(u * k, hd(u) * k); }
      for (let i = n; i >= 0; i--) { const u = -W + 2 * W * i / n; sh.lineTo(u * k, -hd(u) * k); }
      return sh;
    };
    const slab = (k, depth, mat, y) => {
      const g = new THREE.ExtrudeGeometry(lens(k), { depth, bevelEnabled: false }); g.rotateX(-Math.PI / 2);
      add(g, mat, x, y, z);
    };
    const sand = lambert(0xe3d2ad), sandDark = lambert(0xcbb68c);
    slab(1, h, sand, 0);
    // Лоджии: тёмные ниши сеткой по обоим выпуклым фасадам, между ними — рёбра.
    const ribs = [];
    for (let u = -W + 1.4; u < W - 1; u += 2.3) {
      const slope = Math.atan(-2 * 5.5 * u / (W * W));      // наклон кривой фасада
      [1, -1].forEach((side) => {
        const off = side * (hd(u) + 0.08);
        for (let y = 5; y < h - 3; y += 3.3) (rnd() < 0.55 ? winOn : winOff).push({ x: x + u, y, z: z + off, sx: 1.6, sy: 2.2, sz: 0.12, ry: side * slope });
        ribs.push({ x: x + u + 1.15, y: h / 2, z: z + side * (hd(u + 1.15) + 0.2), sx: 0.35, sy: h, sz: 0.5, ry: side * slope });
      });
    }
    instanced(box, sandDark, ribs);
    // Верх: полоса остекления, карниз, корона.
    slab(1.01, 5, lightMat(0x4a4a48, 0xffd890), h);
    slab(1.06, 1.4, sandDark, h + 5);
    slab(0.9, 3, lambert(0xd9a93a), h + 6.4);
    const gold = new THREE.MeshLambertMaterial({ color: 0xe0b040, emissive: 0x3a2800 });
    const spikes = [], spikeGeo = new THREE.ConeGeometry(1.1, 1, 4);
    for (let i = 0; i < 11; i++) {
      const u = -W * 0.8 + 2 * W * 0.8 * i / 10, tall = i % 2 ? 7.5 : 9;
      [1, -1].forEach((side) => spikes.push({ x: x + u, y: h + 9.4 + tall / 2, z: z + side * hd(u) * 0.85, sx: 1, sy: tall, sz: 1 }));
    }
    instanced(spikeGeo, gold, spikes);
    // Подиум с золотыми павильонами.
    add(box, lambert(0xd8cdb4), x, 3, z + 4, 64, 6, 34);
    [[-24, 14], [22, 16]].forEach(([dx, dz]) => {
      add(new THREE.CylinderGeometry(5, 5, 3, 8), sand, x + dx, 7.5, z + dz);
      add(new THREE.ConeGeometry(6, 3, 8), gold, x + dx, 10.5, z + dz);
    });

    // Дворец Республики: белый, синее остекление, широкая плоская крыша.
    const pl = P.palace;
    add(box, lambert(0xd8dde2), pl.x, 1, pl.z, 56, 2, 42);
    add(box, lightMat(0x3f8fb8, 0x9fd8ff), pl.x, 6, pl.z, 44, 8, 30);
    add(box, lambert(0xf2f2ee), pl.x, 12, pl.z, 46, 4, 32);
    add(box, lambert(0xe6e8ea), pl.x, 14.6, pl.z, 58, 1.2, 44);
    add(box, lambert(0xb8c0c8), pl.x, 15.6, pl.z, 30, 1, 20);
  }

  yield 0.43;
  // ─── Парк 28 панфиловцев: Вознесенский собор ─────────────────────────────
  // Жёлтые стены с белыми пилястрами, зелёные крыши, красный цоколь.
  // Купола пёстрые «шахматкой», главы золотые; колокольня с пёстрым шатром.
  {
    const { x, z } = P.cathedral;
    for (let k = 0; k < 120; k++) { const tx = x + rand(-85, 85), tz = z + rand(-80, 80); if (Math.abs(tx - x) > 30 || Math.abs(tz - z) > 42) tree(tx, tz); }
    for (let k = 0; k < 24; k++) spruce(x + rand(-45, 45), z - rand(48, 62));
    const yellow = lambert(0xfbe070), white = lambert(0xf6f3ea), green = lambert(0x3f9a6a), base = lambert(0xa8432f);
    const gold = new THREE.MeshLambertMaterial({ color: 0xe8b83a, emissive: 0x3a2800 });
    // Текстура куполов: ромбы зелёный/красный/жёлтый/синий/белый.
    const cv = document.createElement('canvas'); cv.width = cv.height = 256;
    const g = cv.getContext('2d'), cols = ['#3fae6a', '#d8452f', '#f2d04a', '#3a78c8', '#f6f3ea'];
    g.fillStyle = '#f6f3ea'; g.fillRect(0, 0, 256, 256);
    for (let r = 0; r <= 16; r++) for (let c = 0; c <= 8; c++) {
      g.fillStyle = cols[(r + c * 2) % 4];
      const cx = c * 32 + (r % 2) * 16, cy = r * 16;
      g.beginPath(); g.moveTo(cx, cy - 15); g.lineTo(cx + 15, cy); g.lineTo(cx, cy + 15); g.lineTo(cx - 15, cy); g.closePath(); g.fill();
    }
    const tex = new THREE.CanvasTexture(cv); tex.wrapS = tex.wrapT = THREE.RepeatWrapping; tex.repeat.set(2, 1); tex.colorSpace = THREE.SRGBColorSpace;
    const motley = new THREE.MeshLambertMaterial({ map: tex });
    const gableGeo = new THREE.CylinderGeometry(1, 1, 1, 3); gableGeo.rotateZ(Math.PI / 2);
    const gable = (cx, cy, cz, w, hh, d, alongZ) => { const m = add(gableGeo, green, cx, cy, cz, alongZ ? hh : w, alongZ ? w : hh, d); if (alongZ) m.rotation.y = Math.PI / 2; m.scale.set(alongZ ? d : w, hh, alongZ ? w * 0.6 : d * 0.6); };
    const onion = (px, py, pz, r) => {
      add(new THREE.CylinderGeometry(r * 0.35, r * 0.45, r * 0.8, 10), gold, px, py, pz);
      const o = add(new THREE.SphereGeometry(r * 0.55, 12, 10), gold, px, py + r * 0.9, pz); o.scale.y = 1.3;
      add(new THREE.ConeGeometry(r * 0.25, r * 1.1, 8), gold, px, py + r * 1.8, pz);
      add(box, gold, px, py + r * 2.6, pz, 0.15, r * 1.2, 0.15);
    };
    const dome = (px, py, pz, r) => {
      const d = add(new THREE.SphereGeometry(r, 20, 12, 0, Math.PI * 2, 0, Math.PI / 2), motley, px, py, pz); d.scale.y = 1.25;
      onion(px, py + r * 1.2, pz, r * 0.55);
    };
    // Цоколь, неф вдоль z, трансепт поперёк.
    add(box, lambert(0xcfc3a8), x, 0.3, z, 60, 0.6, 100);
    add(box, base, x, 1.5, z, 28, 3, 50);
    add(box, yellow, x, 9, z, 24, 12, 46);
    add(box, base, x, 1.5, z, 40, 3, 18);
    add(box, yellow, x, 9, z, 38, 12, 16);
    add(box, white, x, 15.3, z, 25, 0.6, 47); add(box, white, x, 15.3, z, 39, 0.6, 17);
    gable(x, 18, z, 24, 5, 46, false); gable(x, 18, z, 38, 5, 16, true);
    // Пилястры и окна.
    const pil = [], wins = [];
    for (let u = -21; u <= 21; u += 6) [12.1, -12.1].forEach((o) => { pil.push({ x: x + o, y: 9, z: z + u, sx: 0.5, sy: 12, sz: 1 }); if (Math.abs(u) > 9) wins.push({ x: x + o * 1.005, y: 9, z: z + u + 3, sx: 0.2, sy: 4, sz: 2 }); });
    for (let u = -18; u <= 18; u += 6) [8.1, -8.1].forEach((o) => { pil.push({ x: x + u, y: 9, z: z + o, sx: 1, sy: 12, sz: 0.5 }); if (Math.abs(u) > 13) wins.push({ x: x + u + 3, y: 9, z: z + o * 1.01, sx: 2, sy: 4, sz: 0.2 }); });
    instanced(box, white, pil);
    instanced(box, lightMat(0x40505a, 0xffd58a), wins);
    // Центральный барабан и большой купол.
    add(new THREE.CylinderGeometry(8, 8, 9, 8), yellow, x, 20, z);
    add(new THREE.CylinderGeometry(8.4, 8.4, 0.6, 8), white, x, 24.6, z);
    dome(x, 24.8, z, 8);
    // Четыре малые главы на барабанах.
    [[-9, -13], [9, -13], [-9, 13], [9, 13]].forEach(([dx, dz]) => {
      add(new THREE.CylinderGeometry(3.4, 3.4, 7, 10), yellow, x + dx, 18, z + dz);
      add(new THREE.CylinderGeometry(3.7, 3.7, 0.5, 10), white, x + dx, 21.6, z + dz);
      dome(x + dx, 21.8, z + dz, 3.4);
    });
    // Колокольня над входом: ярусы, пёстрый шатёр, глава.
    const bz = z + 28;
    add(box, base, x, 1.5, bz, 14, 3, 12);
    add(box, yellow, x, 11, bz, 12, 16, 10);
    add(box, white, x, 19.3, bz, 12.6, 0.6, 10.6);
    add(box, yellow, x, 23, bz, 8, 7, 8);
    add(box, white, x, 26.8, bz, 8.6, 0.6, 8.6);
    add(new THREE.CylinderGeometry(3.2, 3.6, 5, 8), yellow, x, 29.5, bz);
    const tent = add(new THREE.ConeGeometry(3.6, 10, 8), motley, x, 37, bz);
    onion(x, 42, bz, 2.4);
    add(box, lambert(0x6a4a2a), x, 5, bz + 5.1, 3, 6, 0.2);           // двери
    add(box, green, x, 8.5, bz + 6.5, 6, 0.6, 3);                       // козырёк
    // Вечный огонь / мемориал в парке.
    add(box, lambert(0x6b5a4a), x - 50, 6, z - 55, 30, 12, 4);
    add(new THREE.CylinderGeometry(2, 2, 0.8, 16), lightMat(0x8a4a1a, 0xff8a2a), x - 50, 0.8, z - 45);
  }

  yield 0.48;
  // ─── Esentai Tower: стеклянный небоскрёб со скошенным верхом ─────────────
  {
    const { x, z } = P.esentai, h = 150;
    add(box, new THREE.MeshPhongMaterial({ color: 0x6f9fc8, specular: 0xffffff, shininess: 80 }), x, h / 2, z, 30, h, 24);
    const top = add(box, lambert(0x9fc0dc), x, h + 8, z, 30, 16, 24); top.geometry = new THREE.BoxGeometry(1, 1, 1);
    const wedge = new THREE.Shape(); wedge.moveTo(-15, 0); wedge.lineTo(15, 0); wedge.lineTo(15, 22); wedge.lineTo(-15, 6); wedge.closePath();
    const wg = new THREE.ExtrudeGeometry(wedge, { depth: 24, bevelEnabled: false }); wg.translate(0, 0, -12);
    scene.remove(top);
    add(wg, new THREE.MeshPhongMaterial({ color: 0x8fb4d6, specular: 0xffffff, shininess: 80 }), x, h, z);
    const strips = [];
    for (let y = 4; y < h; y += 4) strips.push({ x, y, z, sx: 30.2, sy: 0.25, sz: 24.2 });
    instanced(box, lightMat(0x4f6f90, 0xbfe0ff), strips);
    const l = add(new THREE.SphereGeometry(1.2, 8, 6), lightMat(0x8a2a22, 0xff3b30), x + 14, h + 23, z); redLights.push(l);
    tower(x - 45, z + 20, 30, 24, 40, pick(glassMats));
  }

  yield 0.52;
  // ─── Нурлы Тау: два зеркальных комплекса тёмного стекла по бокам аллеи ──
  // Сзади (юг) — высокие башни со стеклянными пирамидами, к аллее и к улице
  // (север) корпуса спускаются ступенями; углы скошены, крыши — серые вальмы.
  {
    const { x, z } = P.nurlytau;
    // Отражение: небо сверху, светлый горизонт, земля снизу — стекло «видит» мир.
    const env = document.createElement('canvas'); env.width = 256; env.height = 128;
    const eg = env.getContext('2d'), grad = eg.createLinearGradient(0, 0, 0, 128);
    grad.addColorStop(0, '#4f86c6'); grad.addColorStop(0.45, '#b8d4ec'); grad.addColorStop(0.5, '#e8e0cc'); grad.addColorStop(0.56, '#5a6470'); grad.addColorStop(1, '#2a3038');
    eg.fillStyle = grad; eg.fillRect(0, 0, 256, 128);
    const envMap = new THREE.CanvasTexture(env); envMap.mapping = THREE.EquirectangularReflectionMapping; envMap.colorSpace = THREE.SRGBColorSpace;
    const glass = new THREE.MeshPhongMaterial({ color: 0x26303c, specular: 0xffffff, shininess: 120, envMap, reflectivity: 0.6, combine: THREE.MixOperation, flatShading: true });
    const roof = new THREE.MeshPhongMaterial({ color: 0x8a949e, specular: 0xffffff, shininess: 120, envMap, reflectivity: 0.5, combine: THREE.MixOperation, flatShading: true });
    const pyramid = new THREE.MeshPhongMaterial({ color: 0x9fb0c0, specular: 0xffffff, shininess: 140, envMap, reflectivity: 0.75, combine: THREE.MixOperation, flatShading: true });
    const oct = new THREE.CylinderGeometry(1, 1, 1, 8); oct.rotateY(Math.PI / 8);   // корпус со скошенными углами
    const hip = new THREE.ConeGeometry(1, 1, 4); hip.rotateY(Math.PI / 4);           // вальма / пирамида
    const bands = [];
    // Корпус: восьмигранник r × h (по x растянут на sx), крыша — вальма высотой rh.
    const K = 1.3;
    const block = (cx, cz, r, h, sx = 1, rh = 5, roofMat = roof) => {
      cx = x + (cx - x) * K; cz = z + (cz - z) * K; r *= K;
      add(oct, glass, cx, h / 2, cz, r * sx, h, r);
      add(hip, roofMat, cx, h + rh / 2, cz, r * sx * 1.3, rh, r * 1.3);
      for (let y = 4; y < h - 1; y += 3.6) bands.push({ x: cx, y, z: cz, sx: r * sx * 1.01, sy: 0.3, sz: r * 1.01 });
    };
    [-1, 1].forEach((sd) => {
      const bx = x + sd * 26;
      block(bx + sd * 8, z - 34, 12, 96, 1, 17, pyramid);   // главная башня с пирамидой
      block(bx - sd * 8, z - 26, 9, 72, 1, 6);              // соседняя пониже
      block(bx - sd * 10, z - 8, 9, 54, 1, 6);              // средняя, у аллеи
      block(bx - sd * 12, z + 10, 8, 34, 1, 5);             // ступень к улице
      block(bx + sd * 10, z + 20, 10, 24, 1.8, 6);          // широкий низкий корпус
      block(bx + sd * 22, z + 8, 7, 40, 1, 5);              // промежуточная башенка
      const l = add(new THREE.SphereGeometry(1, 8, 6), lightMat(0x8a2a22, 0xff3b30), x + (bx + sd * 8 - x) * K, 96 + 18, z - 34 * K); redLights.push(l);
    });
    instanced(oct, lightMat(0x4a5868, 0xffc070), bands);
    // Аллея между комплексами: мощение, деревья в два ряда, фонтан в центре.
    add(box, lambert(0xc9a88c), x, 0.3, z, 18, 0.4, 90);
    for (let t = -40; t <= 40; t += 7) [-5, 5].forEach((o) => tree(x + o, z + t));
    add(new THREE.CylinderGeometry(6, 6, 1.2, 20), lambert(0xd8d0c4), x, 0.9, z - 20);
    add(new THREE.CylinderGeometry(5.2, 5.2, 0.3, 20), new THREE.MeshPhongMaterial({ color: 0x6fb8d8, shininess: 80 }), x, 1.5, z - 20);
  }

  yield 0.57;
  // ─── КБТУ (бывший Дом правительства): сталинский ампир ───────────────────
  // Высокий центр с колоннадой из 8 белых колонн между пилонами, аттик с
  // гербом, низкие длинные крылья; спереди (юг) — ступени, клумбы, фонтаны.
  {
    const { x, z } = P.kbtu;
    const stone = lambert(0xe2d4b4), stoneDark = lambert(0xc9b996), white = lambert(0xf4f1ea), granite = lambert(0x9a4a3a);
    const gold = new THREE.MeshLambertMaterial({ color: 0xe0b040, emissive: 0x3a2800 });
    const fz = z - 13;                                   // линия главного фасада
    // Центральный корпус и пилоны.
    add(box, stone, x, 16, z, 56, 32, 26);
    [-1, 1].forEach((sd) => add(box, stone, x + sd * 25, 17, fz + 1.5, 9, 34, 5));
    add(box, stoneDark, x, 35, z - 0.5, 62, 2.2, 28);                      // карниз
    add(box, stone, x, 38, z, 58, 4, 25);                                  // аттик
    add(box, stoneDark, x, 40.4, z, 59, 0.8, 26);
    const dent = [];
    for (let u = -30; u <= 30; u += 1.4) dent.push({ x: x + u, y: 33.6, z: fz - 1.4, sx: 0.6, sy: 0.6, sz: 0.6 });
    instanced(box, stoneDark, dent);
    add(new THREE.CylinderGeometry(1.6, 1.6, 0.3, 20), gold, x, 37.5, fz - 0.1).rotation.x = Math.PI / 2;
    // Колоннада: 8 колонн с капителями на красном цоколе.
    add(box, granite, x, 1.5, fz - 4, 58, 3, 8);
    const cols = [], caps = [];
    for (let i = 0; i < 8; i++) { const cx = x - 17.5 + i * 5; cols.push({ x: cx, y: 17, z: fz - 4, sx: 1, sy: 28, sz: 1 }); caps.push({ x: cx, y: 31.6, z: fz - 4, sx: 3, sy: 1.4, sz: 3 }); }
    instanced(new THREE.CylinderGeometry(1.2, 1.35, 1, 14), white, cols);
    instanced(box, white, caps);
    add(box, stone, x, 32.8, fz - 4, 50, 1.4, 8.5);                        // архитрав над колоннами
    // Ступени к входу.
    for (let k = 0; k < 4; k++) add(box, lambert(0xcfc3aa), x, 0.3 + k * 0.4, fz - 13 + k * 2, 44 - k * 2, 0.6 + k * 0.8, 4);
    add(box, lambert(0xd9a040), x, 4, fz - 0.1, 3, 6, 0.3);               // золотые двери
    // Крылья: пониже, длинные, и назад по бокам.
    [-1, 1].forEach((sd) => {
      add(box, stone, x + sd * 52, 12, z + 1, 44, 24, 22);
      add(box, stoneDark, x + sd * 52, 24.8, z + 1, 45, 1.6, 23);
      add(box, stone, x + sd * 66, 12, z + 30, 16, 24, 40);
      add(box, stoneDark, x + sd * 66, 24.8, z + 30, 17, 1.6, 41);
      add(box, granite, x + sd * 52, 1, fz + 1.8, 44, 2, 0.4);
    });
    // Окна: фасад центра (за колоннами) и крыльев, торцы задних крыльев.
    const wins = [];
    for (let y = 6; y < 30; y += 5) {
      for (let u = -18; u <= 18; u += 5) wins.push({ x: x + u + 2.5, y, z: fz - 0.05, sx: 1.8, sy: 2.8, sz: 0.1 });
      for (const sd of [-1, 1]) for (let u = 33; u <= 72; u += 4) if (y < 22) wins.push({ x: x + sd * u, y, z: z + 1 - 11.05, sx: 1.6, sy: 2.6, sz: 0.1 });
    }
    wins.forEach((w) => (rnd() < 0.5 ? winOn : winOff).push(w));
    // Ограждение на крыше и флагштоки.
    add(box, lambert(0x8aa0b8), x, 41.8, fz + 1, 50, 2, 0.2);
    [[0, 12], [-22, 7], [22, 7]].forEach(([dx, hh]) => {
      add(box, lambert(0x999999), x + dx, 40.8 + hh / 2, z, 0.25, hh, 0.25);
      if (dx === 0) add(box, lambert(0x2aa5d9), x + 1.6, 40.8 + hh - 1.2, z, 3, 2, 0.1);
    });
    // Сквер перед зданием: газон, дорожки, красные и жёлтые клумбы, фонтаны.
    const gz = fz - 42;
    add(box, lambert(0x5aa048), x, 0.2, gz, 70, 0.3, 46);
    [-1, 1].forEach((sd) => { const pth = add(box, lambert(0xc9a88c), x + sd * 22, 0.4, gz, 4, 0.2, 48); pth.rotation.y = sd * 0.35; });
    const red = [], yel = [];
    [[0, -10, 22, 7], [-11, 6, 8, 3], [11, 6, 8, 3], [0, 14, 14, 3]].forEach(([dx, dz, rx, rz]) => {
      for (let k = 0; k < rx * rz * 3; k++) {
        const a = rnd() * Math.PI * 2, r = Math.sqrt(rnd());
        const px = x + dx + Math.cos(a) * rx * r, pz = gz + dz + Math.sin(a) * rz * r;
        (r > 0.82 ? yel : red).push({ x: px, y: 0.6, z: pz, sx: 1, sy: 0.7, sz: 1 });
      }
    });
    instanced(box, lambert(0xd9262e), red);
    instanced(box, lambert(0xe8d84a), yel);
    const jets = [];
    [-1, 1].forEach((sd) => {
      add(new THREE.CylinderGeometry(9, 9, 0.4, 24), new THREE.MeshPhongMaterial({ color: 0x8fc8e0, shininess: 80 }), x + sd * 30, 0.4, fz - 16);
      for (let k = 0; k < 14; k++) { const a = k / 14 * Math.PI * 2; jets.push({ x: x + sd * 30 + Math.cos(a) * 6, y: 2.5, z: fz - 16 + Math.sin(a) * 6, sx: 0.3, sy: 4.5, sz: 0.3 }); }
    });
    instanced(box, lightMat(0xeaf6ff, 0xbfe8ff), jets);
    [-1, 1].forEach((sd) => { tree(x + sd * 40, fz - 10); tree(x + sd * 46, fz - 30); });
  }

  yield 0.62;
  // ─── Озеро Сайран: вода, пляж, набережная, мыс с корабликом ──────────────
  {
    const { x, z } = P.sairan;
    const sand = add(new THREE.CircleGeometry(1, 48), lambert(0xe8dcb8), x, 0.15, z, 64, 48, 1); sand.rotation.x = -Math.PI / 2;
    const walk = add(new THREE.RingGeometry(1, 1.08, 48), lambert(0xf0ede4), x, 0.25, z, 66, 50, 1); walk.rotation.x = -Math.PI / 2;
    const water = add(new THREE.CircleGeometry(1, 48), new THREE.MeshPhongMaterial({ color: 0x7fbfa6, specular: 0xffffff, shininess: 60 }), x, 0.35, z, 58, 41, 1);
    water.rotation.x = -Math.PI / 2;
    const cape = add(new THREE.CircleGeometry(9, 20), lambert(0xe8dcb8), x - 18, 0.45, z - 30); cape.rotation.x = -Math.PI / 2;
    add(box, lambert(0x8a5a2a), x - 18, 1.2, z - 30, 6, 1.6, 2.4);
    add(new THREE.ConeGeometry(2, 4, 3), lambert(0xd9262e), x - 18, 4, z - 30);
    [[-22, -32], [-14, -28], [-20, -26]].forEach(([dx, dz]) => { trunks.push({ x: x + dx, y: 2, z: z + dz, sx: 0.4, sy: 4, sz: 0.4 }); crowns.push({ x: x + dx, y: 6, z: z + dz, sx: 1.6, sy: 5, sz: 1.6 }); });
    const pier = add(box, lambert(0xd8d2c2), x - 20, 0.4, z + 16, 34, 0.4, 2); pier.rotation.y = 0.35;
  }

  yield 0.67;
  // ─── Центральная мечеть: белая, голубой купол, минарет ───────────────────
  {
    const { x, z } = P.mosque;
    add(box, lambert(0xf5f3ee), x, 9, z, 50, 18, 50);
    add(new THREE.CylinderGeometry(14, 14, 8, 24), lambert(0xf5f3ee), x, 22, z);
    add(new THREE.SphereGeometry(14, 24, 12, 0, Math.PI * 2, 0, Math.PI / 2), lambert(0x3f8fd0), x, 26, z);
    add(new THREE.CylinderGeometry(0.3, 0.8, 6, 8), new THREE.MeshLambertMaterial({ color: 0xd9a93a }), x, 42, z);
    [[-24, -24], [24, -24]].forEach(([dx, dz]) => {
      add(new THREE.CylinderGeometry(2, 2.4, 48, 12), lambert(0xf5f3ee), x + dx, 24, z + dz);
      add(new THREE.ConeGeometry(2.6, 7, 12), lambert(0x3f8fd0), x + dx, 51.5, z + dz);
    });
  }

  yield 0.71;
  // ─── Яблоневые сады на северо-западе ─────────────────────────────────────
  {
    for (let r = 0; r < 12; r++) for (let c = 0; c < 17; c++) {
      const x = P.apples.x - 90 + c * 11 + (r % 2) * 5, z = P.apples.z - 70 + r * 12;
      if (Math.hypot(x, z) > ISLAND * 0.88) continue;
      if (onRoad(x, z, 4) || nearRiver(x, z, 2)) continue;
      trunks.push({ x, y: 1.2, z, sx: 0.4, sy: 2.4, sz: 0.4 });
      crowns.push({ x, y: 3.6, z, sx: 3, sy: 2.6, sz: 3, ry: rnd() * 3 });
      for (let k = 0; k < 4; k++) apples.push({ x: x + rand(-2.4, 2.4), y: rand(3, 5.2), z: z + rand(-2.4, 2.4), sx: 0.45, sy: 0.45, sz: 0.45 });
    }
  }

  yield 0.76;
  // ─── Кок-Тобе: телебашня и канатная дорога ───────────────────────────────
  const cable: { cabins: any[]; from?: THREE.Vector3; to?: THREE.Vector3 } = { cabins: [] };
  {
    const { x, z } = KOKTOBE, y0 = height(x, z);
    // Телебашня: гладкий серебристый ствол, две «таблетки» и красно-белая антенна.
    const steel = new THREE.MeshPhongMaterial({ color: 0xc9ced4, specular: 0xffffff, shininess: 50 });
    const white = lambert(0xe9ecef), red = lambert(0xc8372d);
    const H = 170;
    add(box, lambert(0x6a6e73), x, y0 + 2, z, 34, 4, 22);
    add(new THREE.CylinderGeometry(4.2, 5.2, H, 16), steel, x, y0 + H / 2, z);
    const rings = [];
    for (let t = 0.08; t < 1; t += 0.1) rings.push({ x, y: y0 + H * t, z, sx: 1, sy: 1, sz: 1 });
    instanced(new THREE.CylinderGeometry(5.3, 5.3, 0.6, 16), lambert(0x8e949a), rings);
    const pod = (y, r) => {
      add(new THREE.CylinderGeometry(r, r * 0.8, 3, 20), steel, x, y - 2.5, z);
      add(new THREE.CylinderGeometry(r, r, 3.5, 20), lightMat(0x6b5a44, 0xffd08a), x, y + 0.7, z);
      add(new THREE.CylinderGeometry(r * 0.8, r, 1.6, 20), steel, x, y + 3.2, z);
    };
    pod(y0 + H * 0.62, 9.5);
    pod(y0 + H * 0.98, 8);
    add(new THREE.CylinderGeometry(3, 3, 8, 12), steel, x, y0 + H * 0.8, z);
    for (let s = 0; s < 9; s++) add(new THREE.CylinderGeometry(1.1 - s * 0.09, 1.2 - s * 0.09, 7, 8), s % 2 ? white : red, x, y0 + H + 8 + s * 7, z);
    const top = add(new THREE.SphereGeometry(1.2, 8, 6), lightMat(0x8a2a22, 0xff3b30), x, y0 + H + 72, z); redLights.push(top);
    // Парк на вершине (ближе к городу): круглый ресторан, колесо, ангар, карусель.
    const px = x + 30, pz = z + 22, py = height(px, pz);
    add(box, lambert(0xcfc6b6), px, py + 0.4, pz, 70, 0.8, 50);
    add(new THREE.CylinderGeometry(13, 13, 8, 32), lightMat(0x2a2e33, 0xffffff), px - 18, py + 5, pz + 14);
    add(new THREE.CylinderGeometry(13.5, 13.5, 1, 32), lightMat(0xdfe4ea, 0xffffff), px - 18, py + 9.5, pz + 14);
    add(box, lightMat(0xd9b27a, 0xffb040), px - 6, py + 3, pz + 6, 30, 6, 8);
    const hangar = add(new THREE.CylinderGeometry(10, 10, 22, 16, 1, false, 0, Math.PI), lambert(0xe6e8ea), px + 20, py + 1, pz + 8);
    hangar.rotation.z = Math.PI / 2; hangar.rotation.y = Math.PI / 2;
    const wheel = add(new THREE.TorusGeometry(15, 0.7, 6, 40), lightMat(0x8a2a22, 0xff3030), px + 2, py + 18, pz - 6);
    add(new THREE.TorusGeometry(9, 0.5, 6, 32), lightMat(0x8a2a22, 0xff3030), px + 2, py + 18, pz - 6).rotation.y = Math.PI / 2;
    const spokes = [];
    for (let k = 0; k < 12; k++) {
      const a = k / 12 * Math.PI * 2;
      spokes.push({ x: px + 2, y: py + 18 + Math.sin(a) * 7.5, z: pz - 6 + Math.cos(a) * 7.5, sx: 0.25, sy: 0.25, sz: 15, rx: -a });
    }
    spokes.forEach((sp) => { const m = add(box, lightMat(0xb08a2a, 0xffd040), sp.x, sp.y, sp.z, sp.sx, sp.sy, sp.sz); m.rotation.x = sp.rx; });
    [-1, 1].forEach((k) => { const leg = add(box, lambert(0xdddddd), px + 2, py + 9, pz - 6 + k * 6, 0.8, 19, 0.8); leg.rotation.x = k * 0.35; });
    add(new THREE.CylinderGeometry(6, 6, 0.8, 20), lambert(0xf4f4f4), px + 26, py + 1, pz - 14);
    add(new THREE.ConeGeometry(6.5, 3, 12), lightMat(0x5a3aa0, 0xff66cc), px + 26, py + 6, pz - 14);
    const sign = document.createElement('canvas'); sign.width = 256; sign.height = 64;
    const g = sign.getContext('2d'); g.font = 'bold 44px sans-serif'; g.fillStyle = '#3dff7a'; g.textAlign = 'center'; g.fillText('KOKTOBE', 128, 48);
    add(new THREE.PlaneGeometry(22, 5.5), new THREE.MeshBasicMaterial({ map: new THREE.CanvasTexture(sign), transparent: true, side: THREE.DoubleSide }), px - 6, py + 8, pz + 1.9);
    // Канатка: от города к вершине, пилоны и кабинки.
    // Нижняя станция — у Дворца Республики (южнее гостиницы «Казахстан»).
    const from = new THREE.Vector3(P.palace.x + 25, 14, P.palace.z), to = new THREE.Vector3(x + 25, y0 + 10, z + 15);
    cable.from = from; cable.to = to;
    const line = new THREE.BufferGeometry().setFromPoints([from, to]);
    [-1.2, 1.2].forEach((o) => { const l = new THREE.Line(line.clone().translate(0, 0, o), new THREE.LineBasicMaterial({ color: 0x333333 })); scene.add(l); });
    for (let t = 0.1; t < 1; t += 0.1) {
      const p = from.clone().lerp(to, t), g = height(p.x, p.z);
      add(box, lambert(0x8a9098), p.x, (g + p.y) / 2, p.z, 1.4, p.y - g, 1.4);
    }
    add(box, lambert(0xe6e8ea), from.x, 7, from.z, 14, 14, 12);
    const cabinMat = [lambert(0xd8352a), lambert(0x2d6fa8), lambert(0xf2c230)];
    for (let k = 0; k < 14; k++) {
      const c = add(box, cabinMat[k % 3], 0, 0, 0, 2.6, 2.6, 2.6);
      cable.cabins.push({ mesh: c, t: k / 14, dir: k % 2 ? 1 : -1 });
    }
  }
  function updateAnim(dt) {
    cable.cabins.forEach((c) => {
      c.t = (c.t + dt * 0.015) % 1;
      const p = cable.from.clone().lerp(cable.to, c.t);
      c.mesh.position.set(p.x, p.y - 2.2, p.z + c.dir * 1.2);
    });
  }
  updateAnim(0);

  yield 0.81;
  // ─── Медеу: каток в долине, трибуны, плотина выше ────────────────────────
  {
    const { x, z } = MEDEU, y0 = height(x, z);
    const ice = add(new THREE.CircleGeometry(1, 48), lightMat(0xcfe6f4, 0xe6f6ff), x, y0 + 0.6, z);
    ice.rotation.x = -Math.PI / 2; ice.scale.set(38, 24, 1);
    const stands = add(new THREE.CylinderGeometry(52, 44, 10, 48, 1, true), new THREE.MeshLambertMaterial({ color: 0x3f6fb0, side: THREE.DoubleSide }), x, y0 + 5, z);
    stands.scale.z = 0.7;
    add(box, lambert(0xf0f0ea), x - 50, y0 + 8, z, 10, 16, 40);
    [[-40, -30], [40, -30], [-40, 30], [40, 30]].forEach(([dx, dz]) => {
      add(box, lambert(0x9aa0a6), x + dx, y0 + 18, z + dz, 1, 36, 1);
      add(box, lightMat(0x777777, 0xffffe0), x + dx, y0 + 36, z + dz, 4, 2, 2);
    });
    // Плотина — высокая стена поперёк долины выше катка.
    // Стена от дна долины вверх, концы уходят в склоны — не висит в воздухе.
    const dz = z - 150, dy = height(x, dz);
    add(box, lambert(0xa89a86), x, dy + 15, dz, 120, 70, 22);
    add(box, lambert(0xcfc7b8), x, dy + 50.5, dz, 122, 1, 24);
    // Серпантин от катка к Шымбулаку — дорога вверх по долине.
    for (let t = 0; t < 1; t += 0.05) {
      const pz = z + 20 - t * 170, px = x + Math.sin(t * 12) * 25;
      const py = height(px, pz);
      add(box, roadMat, px, py + 0.4, pz, 7, 0.4, 10);
    }
  }
  // Ели по склонам гор.
  for (let k = 0; k < 3200; k++) {
    const x = rand(-ISLAND, ISLAND), z = rand(-ISLAND, HILLS.from + 60), y = height(x, z);
    if (y < 25 || y > 175 || Math.hypot(x, z) > ISLAND) continue;
    if (Math.hypot(x - LAKE.x, z - LAKE.z) < LAKE.r + 8) continue;
    if (Math.hypot(x - MEDEU.x, z - MEDEU.z) < 90) continue;
    spruce(x, z);
  }

  yield 0.86;
  // ─── Большое Алматинское озеро ───────────────────────────────────────────
  {
    const lake = add(new THREE.CircleGeometry(LAKE.r, 40), new THREE.MeshPhongMaterial({ color: 0x3fc6c0, specular: 0xffffff, shininess: 60 }), LAKE.x, 121, LAKE.z);
    lake.rotation.x = -Math.PI / 2; lake.scale.set(1, 0.7, 1);
  }

  bodies.forEach((list, mat) => instanced(box, mat, list));
  instanced(box, winLit, winOn);
  instanced(box, winDark, winOff);
  // Деревья и фонари — мелкими секторами и только в радиусе DETAIL_RANGE от
  // птицы: по всему острову это ~250 тыс. треугольников, а дальше 800 м они
  // в тумане размером в пиксель. Граница в тумане, «выпрыгивания» не видно.
  const DETAIL_SECTOR = 400, DETAIL_RANGE = 800;
  const detail = distanceCuller(DETAIL_RANGE);
  const nearOnly = (geo, material, list) => { if (list.length) detail.add(instancedBySector(scene, geo, material, list, DETAIL_SECTOR)); };
  nearOnly(box, lambert(0x8a6a4a), trunks);
  nearOnly(new THREE.IcosahedronGeometry(1, 0), lambert(0x6f9c4a), crowns.filter((c) => !c.spruce));
  nearOnly(new THREE.ConeGeometry(1, 1, 6), lambert(0x2f5f3a), crowns.filter((c) => c.spruce));
  nearOnly(new THREE.IcosahedronGeometry(1, 0), lambert(0xd8352a), apples);
  nearOnly(box, lambert(0x6a6d73), lampPoles);
  nearOnly(box, lightMat(0x8a8474, 0xffd98a), lampHeads);

  yield 0.90;
  // ─── Звёзды для сбора ────────────────────────────────────────────────────
  const starShape = new THREE.Shape();
  for (let i = 0; i < 10; i++) {
    const a = i / 10 * Math.PI * 2 - Math.PI / 2, r = i % 2 ? 0.45 : 1;
    i ? starShape.lineTo(Math.cos(a) * r, Math.sin(a) * r) : starShape.moveTo(Math.cos(a) * r, Math.sin(a) * r);
  }
  const starGeo = new THREE.ExtrudeGeometry(starShape, { depth: 0.35, bevelEnabled: false }); starGeo.center();
  const starMat = new THREE.MeshLambertMaterial({ color: 0xffd23f, emissive: 0x8a5a00 });
  const pickups = [];
  function star(x, y, z) { const s = add(starGeo, starMat, x, y, z, 2.4, 2.4, 2.4); pickups.push(s); }
  // Звёзды: спираль вверх по телебашне Кок-Тобе, цепочка вдоль канатки,
  // кольцо над Медеу, вдоль Esentai, над собором, кольцо над озером.
  {
    const y0 = height(KOKTOBE.x, KOKTOBE.z);
    for (let i = 0; i < 14; i++) { const a = i * 0.9; star(KOKTOBE.x + Math.cos(a) * 30, y0 + 20 + i * 9, KOKTOBE.z + Math.sin(a) * 30); }
    for (let t = 0.1; t < 1; t += 0.1) { const p = cable.from.clone().lerp(cable.to, t); star(p.x, p.y + 10, p.z); }
    const my = height(MEDEU.x, MEDEU.z);
    for (let i = 0; i < 10; i++) { const a = i / 10 * Math.PI * 2; star(MEDEU.x + Math.cos(a) * 40, my + 25, MEDEU.z + Math.sin(a) * 28); }
    for (let y = 20; y < 160; y += 22) star(P.esentai.x + 22, y, P.esentai.z);
    [['nurlytau', 140], ['nurlytau', 90], ['cathedral', 55], ['hotel', 115], ['mosque', 60], ['kbtu', 40], ['sairan', 20]].forEach(([k, y]) => star(P[k].x, y, P[k].z));
    star(0, 45, 0);
    // Дорожки звёзд между достопримечательностями — чтобы было куда лететь.
    [['hotel', 'cathedral'], ['cathedral', 'mosque'], [null, 'nurlytau'], ['nurlytau', 'esentai'], [null, 'sairan'], ['esentai', 'apples']].forEach(([a, b]) => {
      const A = a ? P[a] : { x: 0, z: 0 }, B = P[b];
      for (let t = 0.15; t < 0.9; t += 0.15) star(A.x + (B.x - A.x) * t, 35 + Math.sin(t * Math.PI) * 25, A.z + (B.z - A.z) * t);
    });
    for (let i = 0; i < 8; i++) { const a = i / 8 * Math.PI * 2; star(LAKE.x + Math.cos(a) * 80, 135, LAKE.z + Math.sin(a) * 56); }
  }


  yield 0.95;
  // ─── Время суток ─────────────────────────────────────────────────────────
  // Ключевые точки: день → закат → ночь.
  const K = [
    { t: 0.0, top: 0x3f7fc4, mid: 0x9cc7e8, horizon: 0xdceaf5, fog: 0xc6dbee, sun: 0xfff2dc, sunI: 1.7, hemi: 1.5, sunAlt: 0.6 },
    { t: 0.5, top: 0x2e3a6e, mid: 0xb0708c, horizon: 0xffb266, fog: 0xd9a07e, sun: 0xffa05a, sunI: 1.5, hemi: 1.1, sunAlt: 0.04 },
    { t: 1.0, top: 0x05081a, mid: 0x0f1838, horizon: 0x2a2748, fog: 0x161b33, sun: 0x8fa6ff, sunI: 0.55, hemi: 0.8, sunAlt: -0.25 },
  ];
  const tmpA = new THREE.Color(), tmpB = new THREE.Color();
  function lerpHex(a, b, k) { return tmpA.setHex(a).lerp(tmpB.setHex(b), k).clone(); }
  function setTime(t) {
    const i = t < 0.5 ? 0 : 1, a = K[i], b = K[i + 1], k = (t - a.t) / (b.t - a.t);
    skyUniforms.top.value.copy(lerpHex(a.top, b.top, k));
    skyUniforms.mid.value.copy(lerpHex(a.mid, b.mid, k));
    skyUniforms.horizon.value.copy(lerpHex(a.horizon, b.horizon, k));
    fog.color.copy(lerpHex(a.fog, b.fog, k));
    const sunColor = lerpHex(a.sun, b.sun, k);
    const alt = THREE.MathUtils.lerp(a.sunAlt, b.sunAlt, k);
    const sunDir = new THREE.Vector3(0.45, Math.max(alt, -0.2), -1).normalize();
    sun.position.copy(sunDir).multiplyScalar(1000);
    sun.color.copy(sunColor); sun.intensity = THREE.MathUtils.lerp(a.sunI, b.sunI, k);
    hemi.intensity = THREE.MathUtils.lerp(a.hemi, b.hemi, k);
    hemi.color.copy(lerpHex(0xffffff, 0x9fb0ff, smooth(0.5, 1, t)));
    skyUniforms.sunDir.value.copy(sunDir);
    skyUniforms.sunColor.value.copy(sunColor);
    skyUniforms.sunStrength.value = 1 - smooth(0.62, 0.8, t);
    // Огни зажигаются с закатом.
    const glow = smooth(0.35, 0.75, t);
    lights.forEach((l) => l.m.color.copy(l.day).lerp(l.night, glow));
    facadeGlow.value = glow;
    nightStars.material.opacity = smooth(0.65, 0.95, t);
    fog.near = THREE.MathUtils.lerp(FOG_NEAR_DAY, FOG_NEAR_NIGHT, smooth(0.5, 1, t));
  }



  yield 0.97;
  // Склейка статики: сотни мешей одного вида в секторе → один draw call.
  // Анимированное (поезда, кабинки), звёзды и огни — как есть.
  mergeStaticMeshes(scene, [sky, nightStars, ...pickups, ...redLights, ...cable.cabins.map((c) => c.mesh)], lights.map((l) => l.m), SECTOR_SIZE);

  setTime(0);
  return {
    height,
    pickups,
    // Старт севернее площади Республики, курс на юг — на стелу, акимат и горы.
    spawn: { x: 0, y: 60, z: 160, yaw: 0 },
    radius: ISLAND * 0.92,
    // Потолок выше гребня гор (до ~370 м): иначе рельеф поднимется над потолком.
    maxY: 400,
    update(dt, elapsed, timeOfDay, cameraPosition) {
      setTime(timeOfDay);
      pickups.forEach((s) => { s.rotation.y = elapsed * 2; });
      updateAnim(dt);
      redLights.forEach((l) => { l.visible = timeOfDay < 0.35 || Math.sin(elapsed * 3) > -0.3; });
      detail.update(cameraPosition);
      sky.position.copy(cameraPosition);
      nightStars.position.copy(cameraPosition);
    },
  };
}
